import type { ReactNode } from 'react'

/**
 * The tree's stages past the giant sequoia: a grove, a woodland, a forest,
 * and at last the Giant Forest (Sam, 22 Oct 2026).
 *
 * The first try added one small seedling per stage at the giant's feet, and
 * Sam saw, rightly, that stage 12 to 25 barely changed: "not much visual
 * diff". So each stage here is the camera pulling further back. The child's
 * own giant always stands in front, in the middle, a little smaller each time
 * because more and more fits in the picture: first a few big sequoias beside
 * it, then rows going back (smaller and paler with distance), and every stage
 * brings one new thing to spot (ferns, a deer, a stream, birds, a fox, hills,
 * a river, mist and an owl, sunbeams, a fallen tunnel log, a valley,
 * snowy peaks and an eagle, and a golden sunset with a rainbow).
 *
 * Kept to sequoia country on purpose: no jungle. The sequoia facts on the
 * page would contradict a jungle, and a child who knows where sequoias grow
 * would notice.
 *
 * `g` is the stage past the sequoia, 1 (a tiny grove) to 14 (the Giant
 * Forest). Every shape is placed with a seeded random number, so a stage
 * draws the same every time.
 */

// Held in constants: tests/test_trace_labels.py reads every literal id= in
// the front end as a provenance label, and these are not.
const SKY_DAY = 'ws-forest-sky-day'
const SKY_GOLD = 'ws-forest-sky-gold'

const BARK = '#9C4A2B'
const LEAF = ['#3F7D3A', '#2F6230', '#4A8A40']
const LEAF_LIGHT = '#5FA052'
const HAZE = '#B9D3C8'

function seeded(seed: number) {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 2 ** 32
  }
}

function mix(a: string, b: string, t: number): string {
  const p = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))
  const [x, y] = [p(a), p(b)]
  return '#' + x.map((v, i) => Math.round(v + (y[i]! - v) * t).toString(16).padStart(2, '0')).join('')
}

/** One sequoia: a red trunk, bare low down, and a column of rounded clumps. */
function sequoia(key: string, x: number, base: number, h: number, far: number, rnd: () => number): ReactNode[] {
  const out: ReactNode[] = []
  const bark = mix(BARK, HAZE, far)
  const tw = Math.max(1.4, h * 0.075)
  out.push(<path key={`${key}t`} fill={bark}
    d={`M${x - tw / 2 - tw * 0.3} ${base} L ${x - tw * 0.2} ${base - h * 0.9} L ${x + tw * 0.2} ${base - h * 0.9} L ${x + tw / 2 + tw * 0.3} ${base} Z`} />)
  if (h < 26) {
    out.push(<ellipse key={`${key}c`} cx={x} cy={base - h * 0.66} rx={h * 0.16} ry={h * 0.36}
      fill={mix(LEAF[1]!, HAZE, far)} />)
    return out
  }
  const clumps = h > 110 ? 6 : 5
  for (let k = 0; k < clumps; k++) {
    const f = k / (clumps - 1)
    const cy = base - h * (0.42 + 0.53 * f)
    const rx = h * 0.17 * (1 - f * 0.45) * (0.9 + rnd() * 0.2)
    const ry = rx * 0.72
    const cx = x + (rnd() - 0.5) * h * 0.05
    const fill = mix(LEAF[Math.floor(rnd() * LEAF.length)]!, HAZE, far)
    out.push(<ellipse key={`${key}c${k}`} cx={cx} cy={cy} rx={rx} ry={ry} fill={fill} />)
    if (far < 0.5) {
      out.push(<ellipse key={`${key}h${k}`} cx={cx - rx * 0.3} cy={cy - ry * 0.35} rx={rx * 0.42} ry={ry * 0.36}
        fill={LEAF_LIGHT} opacity={0.45 * (1 - far)} />)
    }
  }
  return out
}

/** The child's own giant: front, middle, and the tallest in the picture. */
function giant(x: number, base: number, h: number, rnd: () => number): ReactNode[] {
  return sequoia('giant', x, base, h, 0, rnd)
}

interface Row { n: number; base: number; h: [number, number]; far: number }

interface Scene {
  rows: Row[]           // back to front
  giant: number         // the child's tree, front centre
  extras: string[]      // the things to spot, in drawing order
}

// One line per stage past the sequoia. Each adds something new.
const SCENES: Scene[] = [
  /* 1 tiny grove      */ { giant: 250, extras: [], rows: [{ n: 2, base: 372, h: [150, 175], far: 0 }] },
  /* 2 small grove     */ { giant: 235, extras: ['ferns'], rows: [{ n: 4, base: 372, h: [130, 175], far: 0 }] },
  /* 3 grove           */ { giant: 225, extras: ['ferns', 'deer'], rows: [
    { n: 4, base: 346, h: [105, 135], far: 0.35 }, { n: 4, base: 372, h: [140, 180], far: 0 }] },
  /* 4 big grove       */ { giant: 215, extras: ['stream', 'ferns', 'flowers', 'deer'], rows: [
    { n: 6, base: 340, h: [95, 125], far: 0.4 }, { n: 5, base: 372, h: [140, 185], far: 0 }] },
  /* 5 woodland        */ { giant: 205, extras: ['stream', 'bushes', 'flowers', 'birds'], rows: [
    { n: 8, base: 318, h: [70, 92], far: 0.6 }, { n: 6, base: 342, h: [100, 128], far: 0.35 },
    { n: 5, base: 372, h: [115, 150], far: 0 }] },
  /* 6 thick woodland  */ { giant: 195, extras: ['bushes', 'flowers', 'birds', 'fox'], rows: [
    { n: 10, base: 314, h: [68, 92], far: 0.6 }, { n: 8, base: 340, h: [95, 128], far: 0.35 },
    { n: 6, base: 372, h: [112, 148], far: 0 }] },
  /* 7 young forest    */ { giant: 185, extras: ['hills', 'bushes', 'birds', 'fox'], rows: [
    { n: 14, base: 290, h: [42, 58], far: 0.75 }, { n: 11, base: 314, h: [62, 86], far: 0.55 },
    { n: 9, base: 342, h: [92, 122], far: 0.3 }, { n: 6, base: 372, h: [108, 142], far: 0 }] },
  /* 8 forest          */ { giant: 178, extras: ['hills', 'river', 'bushes', 'birds'], rows: [
    { n: 16, base: 288, h: [42, 58], far: 0.75 }, { n: 13, base: 312, h: [62, 86], far: 0.55 },
    { n: 10, base: 340, h: [90, 122], far: 0.3 }, { n: 7, base: 372, h: [106, 140], far: 0 }] },
  /* 9 deep forest     */ { giant: 172, extras: ['hills', 'mist', 'bushes', 'owl'], rows: [
    { n: 18, base: 286, h: [42, 58], far: 0.75 }, { n: 14, base: 311, h: [62, 86], far: 0.55 },
    { n: 11, base: 339, h: [90, 122], far: 0.3 }, { n: 7, base: 372, h: [104, 138], far: 0 }] },
  /* 10 dense forest   */ { giant: 168, extras: ['hills', 'mist', 'sunbeams', 'bushes', 'owl'], rows: [
    { n: 22, base: 284, h: [44, 60], far: 0.72 }, { n: 17, base: 309, h: [64, 88], far: 0.52 },
    { n: 13, base: 337, h: [92, 124], far: 0.28 }, { n: 8, base: 372, h: [104, 138], far: 0 }] },
  /* 11 old growth     */ { giant: 255, extras: ['hills', 'sunbeams', 'log', 'ferns'], rows: [
    { n: 18, base: 286, h: [55, 72], far: 0.72 }, { n: 13, base: 312, h: [85, 110], far: 0.5 },
    { n: 9, base: 340, h: [130, 165], far: 0.26 }, { n: 6, base: 372, h: [180, 225], far: 0 }] },
  /* 12 forest valley  */ { giant: 150, extras: ['mountains', 'valley-river', 'birds'], rows: [
    { n: 26, base: 262, h: [24, 32], far: 0.8 }, { n: 22, base: 282, h: [34, 46], far: 0.68 },
    { n: 18, base: 306, h: [50, 66], far: 0.5 }, { n: 13, base: 336, h: [76, 100], far: 0.3 },
    { n: 7, base: 372, h: [92, 120], far: 0 }] },
  /* 13 forest giants  */ { giant: 160, extras: ['mountains', 'snow', 'valley-river', 'eagle'], rows: [
    { n: 28, base: 262, h: [26, 34], far: 0.8 }, { n: 24, base: 282, h: [36, 48], far: 0.68 },
    { n: 19, base: 306, h: [54, 70], far: 0.5 }, { n: 14, base: 336, h: [82, 106], far: 0.3 },
    { n: 7, base: 372, h: [96, 124], far: 0 }] },
  /* 14 Giant Forest   */ { giant: 170, extras: ['gold', 'mountains', 'snow', 'rainbow', 'valley-river', 'eagle', 'deer'], rows: [
    { n: 30, base: 262, h: [26, 34], far: 0.78 }, { n: 25, base: 282, h: [36, 48], far: 0.66 },
    { n: 20, base: 306, h: [54, 70], far: 0.48 }, { n: 15, base: 336, h: [82, 108], far: 0.28 },
    { n: 8, base: 372, h: [96, 126], far: 0 }] },
]

export const FOREST_STAGES = SCENES.length

function deer(key: string, x: number, y: number, s: number): ReactNode {
  const c = '#A0673A'
  return (
    <g key={key} transform={`translate(${x} ${y}) scale(${s})`}>
      <path d="M-14 -2 L-14 10 M-9 -1 L-9 10 M7 -1 L7 10 M11 -2 L11 10" stroke={c} strokeWidth={2.2} strokeLinecap="round" />
      <ellipse cx={-1} cy={-6} rx={15} ry={7} fill={c} />
      <path d="M10 -9 L16 -22 L21 -20 L16 -7 Z" fill={c} />
      <ellipse cx={20} cy={-23} rx={5.5} ry={4} fill={c} />
      <path d="M17 -26 L15 -31 M21 -26 L22 -31" stroke={c} strokeWidth={1.6} strokeLinecap="round" />
      <circle cx={21} cy={-24} r={0.9} fill="#2E2140" />
      <ellipse cx={-15} cy={-8} rx={2.5} ry={3} fill="#FFFFFF" opacity={0.85} />
    </g>
  )
}

function fox(key: string, x: number, y: number): ReactNode {
  const c = '#E27A2E'
  return (
    <g key={key} transform={`translate(${x} ${y})`}>
      <path d="M-18 -6 C-28 -10 -30 -2 -24 2 C-20 4 -16 0 -13 -3 Z" fill={c} />
      <path d="M-26 0 C-25 -2 -23 -2 -22 0 Z" fill="#FFFFFF" />
      <ellipse cx={-4} cy={-5} rx={11} ry={5.5} fill={c} />
      <path d="M-10 -1 L-10 5 M-2 -1 L-2 5 M4 -1 L4 5" stroke="#5A2E14" strokeWidth={1.8} strokeLinecap="round" />
      <path d="M5 -9 L15 -6 L8 -1 Z" fill={c} />
      <path d="M6 -9 L7 -15 L10 -9 Z M10 -8 L13 -13 L14 -7 Z" fill={c} />
      <circle cx={10} cy={-7} r={0.8} fill="#2E2140" />
    </g>
  )
}

function owl(key: string, x: number, y: number): ReactNode {
  return (
    <g key={key} transform={`translate(${x} ${y})`}>
      <ellipse cx={0} cy={0} rx={6} ry={8} fill="#8A6A4A" />
      <path d="M-5 -6 L-6 -11 L-2 -7 Z M5 -6 L6 -11 L2 -7 Z" fill="#8A6A4A" />
      <circle cx={-2.4} cy={-3} r={2.4} fill="#FFF6C8" /><circle cx={2.4} cy={-3} r={2.4} fill="#FFF6C8" />
      <circle cx={-2.4} cy={-3} r={1} fill="#2E2140" /><circle cx={2.4} cy={-3} r={1} fill="#2E2140" />
      <path d="M-1 0 L0 2 L1 0 Z" fill="#E0A82E" />
    </g>
  )
}

function eagle(key: string, x: number, y: number): ReactNode {
  return (
    <g key={key} transform={`translate(${x} ${y})`}>
      <path d="M-22 0 C-14 -8 -6 -6 0 -1 C6 -6 14 -8 22 0 C14 -3 6 -1 0 3 C-6 -1 -14 -3 -22 0 Z" fill="#3B2A20" />
      <circle cx={0} cy={-1} r={2.6} fill="#FFFFFF" />
    </g>
  )
}

function birds(key: string, rnd: () => number): ReactNode[] {
  return [0, 1, 2].map((i) => {
    const x = 60 + i * 26 + rnd() * 10
    const y = 70 + i * 9 + rnd() * 8
    return <path key={`${key}${i}`} d={`M${x} ${y} q 4 -5 8 0 q 4 -5 8 0`} stroke="#2E2140" strokeWidth={1.6} fill="none" strokeLinecap="round" />
  })
}

export function ForestScene({ g }: { g: number }) {
  const scene = SCENES[Math.max(0, Math.min(SCENES.length, g) - 1)]!
  const rnd = seeded(g * 131 + 7)
  const has = (x: string) => scene.extras.includes(x)
  const gold = has('gold')
  const back: ReactNode[] = []
  const land: ReactNode[] = []
  const front: ReactNode[] = []

  // The sky, the sun, and anything far away.
  if (has('sunbeams')) {
    for (let i = 0; i < 4; i++) {
      back.push(<path key={`beam${i}`} d={`M${230 + i * 14} 30 L ${40 + i * 60} 380 L ${70 + i * 60} 380 Z`}
        fill="#FFF3B0" opacity={0.18} />)
    }
  }
  if (has('rainbow')) {
    ['#E2557A', '#F57C1F', '#F6C343', '#4CC3A0', '#5AB4E5', '#9B7BE0'].forEach((c, i) => {
      back.push(<path key={`rb${i}`} d={`M ${30 + i * 6} 250 A ${130 - i * 6} ${130 - i * 6} 0 0 1 ${290 - i * 6} 250`}
        stroke={c} strokeWidth={6} fill="none" opacity={0.45} />)
    })
  }
  if (has('mountains')) {
    back.push(
      <path key="mtA" d="M0 270 L 40 170 L 75 215 L 120 130 L 165 230 L 190 200 L 235 120 L 280 190 L 320 150 L 320 300 L 0 300 Z"
        fill={gold ? '#B98FA0' : '#9DB4C8'} />,
      <path key="mtB" d="M0 285 L 55 215 L 95 255 L 150 195 L 205 260 L 250 205 L 320 250 L 320 300 L 0 300 Z"
        fill={gold ? '#A78C8A' : '#86A39A'} />,
    )
    if (has('snow')) {
      back.push(
        <path key="snA" d="M108 146 L 120 130 L 132 150 L 125 146 L 119 152 L 114 145 Z" fill="#FFFFFF" />,
        <path key="snB" d="M222 138 L 235 120 L 249 141 L 241 137 L 235 144 L 229 136 Z" fill="#FFFFFF" />,
        <path key="snC" d="M31 190 L 40 170 L 50 188 L 44 185 L 40 190 Z" fill="#FFFFFF" />,
      )
    }
  } else if (has('hills')) {
    back.push(<path key="hill" d="M0 300 C 60 255 120 262 170 280 C 220 295 270 250 320 262 L 320 320 L 0 320 Z"
      fill="#A9C9A4" />)
  }

  // Rows of sequoias, the far ones first so the near ones stand in front.
  scene.rows.forEach((row, r) => {
    land.push(<path key={`ground${r}`}
      d={`M0 ${row.base - 4} C 80 ${row.base - 9}, 160 ${row.base + 1}, 240 ${row.base - 6} S 320 ${row.base - 3}, 320 ${row.base - 4} L 320 400 L 0 400 Z`}
      fill={mix('#8FC46A', HAZE, row.far * 0.8)} />)
    if (r === scene.rows.length - 1 && (has('river') || has('valley-river'))) {
      // The river comes out of the far trees and runs down to the front.
      land.push(<path key="river" fill="#8CCBEB" opacity={0.95}
        d="M150 262 C 160 290, 120 310, 140 330 C 160 350, 110 370, 120 400 L 175 400 C 165 372, 205 352, 180 330 C 160 310, 190 290, 162 262 Z" />)
    }
    const span = 300 / row.n
    for (let i = 0; i < row.n; i++) {
      const x = 10 + span * (i + 0.5) + (rnd() - 0.5) * span * 0.5
      // Keep the front row clear of the giant in the middle.
      if (row.far === 0 && Math.abs(x - 160) < 34) continue
      const h = row.h[0] + rnd() * (row.h[1] - row.h[0])
      land.push(...sequoia(`r${r}-${i}`, x, row.base + (rnd() - 0.5) * 4, h, row.far, rnd))
    }
    if (has('mist') && r < scene.rows.length - 1) {
      land.push(<rect key={`mist${r}`} x={0} y={row.base - 18} width={320} height={22} fill="#FFFFFF" opacity={0.32} />)
    }
  })

  // Things on the ground in front.
  if (has('stream')) {
    front.push(<path key="stream" d="M0 384 C 60 376, 110 392, 170 384 S 270 376, 320 386 L 320 394 C 270 386, 220 398, 170 392 S 60 386, 0 392 Z"
      fill="#8CCBEB" />)
  }
  if (has('log')) {
    front.push(
      <rect key="log" x={196} y={350} width={96} height={22} rx={11} fill="#8A4A2A" />,
      <ellipse key="logend" cx={292} cy={361} rx={8} ry={11} fill="#C98A5A" />,
      <ellipse key="loghole" cx={292} cy={363} rx={4.5} ry={6} fill="#3B2216" />,
      <path key="moss" d="M200 352 C 220 346, 240 354, 260 348 C 270 346, 280 350, 288 349 L 288 354 L 200 356 Z" fill="#6FAE4A" />,
    )
  }
  if (has('ferns') || has('bushes')) {
    for (let i = 0; i < 9; i++) {
      const x = 14 + i * 36 + rnd() * 10
      if (Math.abs(x - 160) < 22) continue
      if (has('bushes')) {
        front.push(<ellipse key={`bu${i}`} cx={x} cy={382} rx={13} ry={8} fill={LEAF[i % 3]} />)
      } else {
        front.push(<path key={`fe${i}`} d={`M${x} 386 q -10 -10 -14 -2 M${x} 386 q 10 -10 14 -2 M${x} 386 q -2 -14 0 -16`}
          stroke={LEAF_LIGHT} strokeWidth={2.4} fill="none" strokeLinecap="round" />)
      }
    }
  }
  if (has('flowers')) {
    for (let i = 0; i < 10; i++) {
      const x = 12 + i * 31 + rnd() * 8
      if (Math.abs(x - 160) < 20) continue
      front.push(<circle key={`fl${i}`} cx={x} cy={391 - rnd() * 4} r={2.6} fill={['#F6C343', '#E2557A', '#FFFFFF'][i % 3]} />)
    }
  }
  if (has('deer')) front.push(deer('deer', 66, 372, gold ? 0.8 : 1))
  if (has('fox')) front.push(fox('fox', 250, 386))
  if (has('owl')) front.push(<g key="owlbig" transform="translate(112 262) scale(1.8)">{owl('owl', 0, 0)}</g>)
  if (has('birds')) front.push(...birds('bird', rnd))
  if (has('eagle')) front.push(eagle('eagle', 92, 92))

  // The child's own giant, always in front and in the middle.
  const giantTree = giant(160, 374, scene.giant, rnd)

  return (
    <svg viewBox="0 0 320 400" role="img" aria-label="A sequoia forest"
      style={{ width: '100%', maxWidth: 420, display: 'block', margin: '0 auto' }}>
      <defs>
        <linearGradient id={SKY_DAY} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#CDEBFF" /><stop offset="1" stopColor="#FFF7E6" />
        </linearGradient>
        <linearGradient id={SKY_GOLD} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFC98A" /><stop offset="0.6" stopColor="#FFE6B8" /><stop offset="1" stopColor="#FFF4DE" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="320" height="400" rx="18" fill={`url(#${gold ? SKY_GOLD : SKY_DAY})`} />
      <circle cx={gold ? 250 : 268} cy={gold ? 150 : 56} r={gold ? 30 : 22} fill={gold ? '#FFB347' : '#FFD25A'} opacity={0.92} />
      <g fill="#FFFFFF" opacity={0.9}>
        <ellipse cx="72" cy="52" rx="32" ry="11" /><ellipse cx="94" cy="45" rx="20" ry="11" />
      </g>
      <g className="ws-grow" style={{ transformOrigin: '160px 372px' }}>
        {back}{land}{giantTree}{front}
      </g>
    </svg>
  )
}
